/**
 * geminiService.js — Gemini API client for the AI Template Builder
 *
 * Talks to either:
 *   1. A Cloudflare Worker proxy (REACT_APP_GEMINI_WORKER_URL), or
 *   2. The Gemini API directly (REACT_APP_GEMINI_KEY) — dev/demo fallback
 *
 * All calls use Gemini 3.5 Flash (multimodal: text + images).
 */

const WORKER_URL = process.env.REACT_APP_GEMINI_WORKER_URL || '';
const DIRECT_KEY = process.env.REACT_APP_GEMINI_KEY || '';
const MODEL = 'gemini-3.5-flash';

// ---------------------------------------------------------------------------
// System prompt — encodes template schema + domain context
// ---------------------------------------------------------------------------

export const SYSTEM_PROMPT = `You are a process engineering assistant for LiTeCAB, a life-cycle and techno-economic analysis tool for energy and materials technologies (batteries, recycling, manufacturing, catalysts, hydrogen, solar, etc.).

Your job: convert a user's process description (text, document, or image) into a structured JSON template that LiTeCAB can load.

IMPORTANT RULES:
1. Return ONLY valid JSON. No markdown fences. No explanatory text outside the JSON.
2. Every process must have a unique ID like "P001", "P002", etc.
3. Use realistic quantities, units, and cost estimates when possible. If uncertain, use reasonable placeholder values and add a note in the description.
4. The "next_process_ids" array defines the process flow graph.
5. Do NOT include traci_scores_per_unit — that requires licensed databases and will be added separately.

TEMPLATE JSON SCHEMA:
{
  "project_name": "string — descriptive name",
  "description": "string — one-line summary",
  "version": "1.0",
  "functional_unit": {
    "description": "string — e.g. '1 kg of cathode active material produced'",
    "scale": "string — lab | pilot | industrial",
    "weight_kg": number,
    "feedstock_type": "string"
  },
  "processes": {
    "P001": {
      "id": "P001",
      "name": "string — PascalCase_With_Underscores",
      "process_type": "string — e.g. Preparation, Synthesis, Separation, Purification, Assembly, Coating, Drying, etc.",
      "manufacturing_category": "string — Manufacturing | Recycling | Remanufacturing",
      "hierarchy_level": 0,
      "is_side_input": false,
      "energy_input_kwh": number,
      "equipment": "string — equipment name or description",
      "parameters": {
        "duration_hours": number,
        "temperature_c": number,
        "pressure_atm": number
      },
      "next_process_ids": ["P002"],
      "description": "string — what this step does",
      "products": [
        { "name": "string", "quantity": number, "unit": "string", "price_per_unit": number }
      ],
      "lci_inputs": [
        {
          "name": "string — material or energy name",
          "quantity": number,
          "unit": "string — e.g. kg, kilowatt hour, liter",
          "cost_per_unit": number,
          "cost_unit": "string — e.g. USD/kg, USD/kWh"
        }
      ]
    }
  }
}

EXAMPLE (abbreviated, 2 processes of a recycling workflow):
{
  "project_name": "Li-NMC Battery Recycling",
  "description": "Hydrometallurgical recycling of end-of-life Li-NMC battery cells",
  "version": "1.0",
  "functional_unit": {
    "description": "1 end-of-life Li-NMC battery module processed (180 kg)",
    "scale": "industrial",
    "weight_kg": 180,
    "feedstock_type": "End-of-life Li-NMC battery cells"
  },
  "processes": {
    "P001": {
      "id": "P001",
      "name": "Battery_Discharging",
      "process_type": "Preparation",
      "manufacturing_category": "Recycling",
      "hierarchy_level": 0,
      "is_side_input": false,
      "energy_input_kwh": 89.1,
      "equipment": "Discharge_Station_380V",
      "parameters": { "duration_hours": 2.5, "temperature_c": 25, "pressure_atm": 1 },
      "next_process_ids": ["P002"],
      "description": "Full discharge of battery cells for safe handling",
      "products": [],
      "lci_inputs": [
        { "name": "Electricity US Market", "quantity": 89.1, "unit": "kilowatt hour", "cost_per_unit": 0.071, "cost_unit": "USD/kWh" }
      ]
    },
    "P002": {
      "id": "P002",
      "name": "Mechanical_Shredding",
      "process_type": "Separation",
      "manufacturing_category": "Recycling",
      "hierarchy_level": 1,
      "is_side_input": false,
      "energy_input_kwh": 15.0,
      "equipment": "Industrial_Shredder_50kW",
      "parameters": { "duration_hours": 1, "temperature_c": 25, "pressure_atm": 1 },
      "next_process_ids": [],
      "description": "Mechanical shredding of discharged cells into black mass",
      "products": [
        { "name": "Black Mass", "quantity": 120, "unit": "kg", "price_per_unit": null }
      ],
      "lci_inputs": [
        { "name": "Electricity US Market", "quantity": 15.0, "unit": "kilowatt hour", "cost_per_unit": 0.071, "cost_unit": "USD/kWh" }
      ]
    }
  }
}

When the user asks you to modify an existing template, return the COMPLETE updated template JSON (not just the diff).`;

const DEFAULT_ACK = 'Understood. I will generate valid LiTeCAB template JSON based on your input.';

// ---------------------------------------------------------------------------
// Build Gemini-format contents array from chat history
// ---------------------------------------------------------------------------

function buildContents(messages, systemPrompt = SYSTEM_PROMPT, systemAck = DEFAULT_ACK) {
  // messages: [{ role: 'user'|'assistant', text?, imageBase64?, imageMime? }]
  const contents = [];

  // System instruction as the first "user" turn (Gemini format)
  contents.push({
    role: 'user',
    parts: [{ text: systemPrompt }],
  });
  contents.push({
    role: 'model',
    parts: [{ text: systemAck }],
  });

  for (const msg of messages) {
    const parts = [];
    if (msg.text) parts.push({ text: msg.text });
    if (msg.imageBase64 && msg.imageMime) {
      parts.push({
        inlineData: { mimeType: msg.imageMime, data: msg.imageBase64 },
      });
    }
    if (parts.length > 0) {
      contents.push({
        role: msg.role === 'assistant' ? 'model' : 'user',
        parts,
      });
    }
  }

  return contents;
}

// ---------------------------------------------------------------------------
// Call Gemini (via Worker proxy or direct)
// ---------------------------------------------------------------------------

export async function callGemini(messages, options = {}) {
  const { systemPrompt = SYSTEM_PROMPT, systemAck = DEFAULT_ACK, generationConfig } = options;
  const contents = buildContents(messages, systemPrompt, systemAck);
  const payload = {
    contents,
    generationConfig: generationConfig || {
      temperature: 0.2,
      responseMimeType: 'application/json',
    },
  };

  let url;
  if (WORKER_URL) {
    url = WORKER_URL;
  } else if (DIRECT_KEY) {
    url = `https://generativelanguage.googleapis.com/v1beta/models/${MODEL}:generateContent?key=${DIRECT_KEY}`;
  } else {
    throw new Error('No Gemini API configuration found. Set REACT_APP_GEMINI_WORKER_URL or REACT_APP_GEMINI_KEY.');
  }

  const resp = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!resp.ok) {
    const errText = await resp.text();
    if (resp.status === 429) {
      throw new Error('Rate limited — please wait a moment and try again.');
    }
    throw new Error(`Gemini API error (${resp.status}): ${errText}`);
  }

  const data = await resp.json();

  // Extract text from Gemini response
  const text =
    data?.candidates?.[0]?.content?.parts?.[0]?.text ||
    data?.candidates?.[0]?.content?.parts
      ?.filter((p) => p.text)
      .map((p) => p.text)
      .join('') ||
    '';

  if (!text) {
    throw new Error('Empty response from Gemini.');
  }

  return text;
}

// ---------------------------------------------------------------------------
// Parse Gemini response text into a template object
// ---------------------------------------------------------------------------

export function parseTemplateResponse(text) {
  // Strip markdown fences if Gemini included them despite instructions
  let clean = text.trim();
  if (clean.startsWith('```')) {
    clean = clean.replace(/^```(?:json)?\s*/, '').replace(/\s*```$/, '');
  }

  const template = JSON.parse(clean);

  // Basic validation
  if (!template.project_name) throw new Error('Missing project_name in generated template.');
  if (!template.processes || Object.keys(template.processes).length === 0) {
    throw new Error('Generated template has no processes.');
  }

  return template;
}
